import { Directive, ElementRef, Input, AfterViewInit, OnDestroy, OnChanges } from '@angular/core';
import { ServiceService } from '../service.service';
import * as L from 'leaflet';
import 'leaflet.heat';
import 'leaflet.markercluster';
import 'leaflet-simple-map-screenshoter';

/**
 * Renders a salesperson's visit heat-map (getVisitMap payload) into the host
 * element using Leaflet (free, no API key). Bind [visitMap]="msg.mapData".
 *
 * Features:
 *  - Street (OpenStreetMap) ⇄ Satellite (Esri World Imagery) toggle. CartoDB is
 *    DNS-blocked from this network, so Street stays on OSM (globally reachable).
 *  - Subtle heat glow + COUNT-BUBBLE markers that cluster (overlapping pins merge
 *    into one bubble showing the SUM of visits, and split apart as you zoom).
 *  - A toolbar with ⛶ FULL SCREEN (native Fullscreen API) and ⤓ DOWNLOAD PNG
 *    (captures the live map — basemap + heat + bubbles — client-side via
 *    leaflet-simple-map-screenshoter / dom-to-image). Tiles load with crossOrigin
 *    so the capture canvas isn't tainted (OSM + Esri both send
 *    Access-Control-Allow-Origin: *).
 *
 * Inline-styled divIcons/controls are used throughout so there are no
 * marker-image webpack 404s and no Angular CSS-encapsulation issues (Leaflet
 * injects its DOM outside the component's encapsulated styles).
 */
@Directive({ selector: '[visitMap]' })
export class VisitMapDirective implements AfterViewInit, OnDestroy, OnChanges {
  @Input('visitMap') data: any;
  private map: L.Map | null = null;
  private inited = false;
  private shoter: any = null;
  private fsHandler: (() => void) | null = null;
  private resizeObserver: ResizeObserver | null = null;

  constructor(private el: ElementRef<HTMLElement>, private api: ServiceService) {}
ngAfterViewInit(): void { setTimeout(() => this.render(), 60); }
  ngOnChanges(): void { if (this.inited) { this.destroy(); setTimeout(() => this.render(), 60); } }
  ngOnDestroy(): void { this.destroy(); }

  private destroy(): void {
    if (this.fsHandler) {
      document.removeEventListener('fullscreenchange', this.fsHandler);
      document.removeEventListener('webkitfullscreenchange', this.fsHandler as any);
      this.fsHandler = null;
    }
    if (this.resizeObserver) { try { this.resizeObserver.disconnect(); } catch {} this.resizeObserver = null; }
    this.shoter = null;
    if (this.map) { try { this.map.remove(); } catch { /* noop */ } this.map = null; }
    this.inited = false;
  }

  private bubble(count: number, size: number): L.DivIcon {
    const fs = Math.max(10, Math.round(size * 0.4));
    return L.divIcon({
      className: '',
      html: `<div style="width:${size}px;height:${size}px;line-height:${size}px;background:rgba(22,163,74,0.92);color:#fff;border:2px solid #fff;border-radius:50%;text-align:center;font-weight:700;font-size:${fs}px;box-shadow:0 1px 4px rgba(0,0,0,0.4);">${count}</div>`,
      iconSize: [size, size], iconAnchor: [size / 2, size / 2], popupAnchor: [0, -size / 2]
    });
  }

  /** A small solid colour-coded pin (no number) for category point maps. */
  private dot(color: string, size: number): L.DivIcon {
    return L.divIcon({
      className: '',
      html: `<div style="width:${size}px;height:${size}px;background:${color};border:2px solid #fff;border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,0.45);"></div>`,
      iconSize: [size, size], iconAnchor: [size / 2, size / 2], popupAnchor: [0, -size / 2]
    });
  }

  /** Colour legend (bottom-right) for category point maps. */
  private legendControl(legend: { label: string; color: string; count?: number }[]): L.Control {
    const Ctrl = L.Control.extend({
      options: { position: 'bottomright' as L.ControlPosition },
      onAdd: () => {
        const box = L.DomUtil.create('div');
        box.style.cssText = 'background:rgba(255,255,255,0.95);padding:6px 9px;border-radius:8px;box-shadow:0 1px 5px rgba(0,0,0,0.25);font-size:11px;line-height:1.55;max-height:180px;overflow:auto;';
        box.innerHTML = legend.map(l =>
          `<div style="white-space:nowrap;"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${l.color};margin-right:5px;vertical-align:middle;"></span>${String(l.label).replace(/</g, '&lt;')}${l.count != null ? ` <span style="color:#6b7280;">(${l.count})</span>` : ''}</div>`
        ).join('');
        L.DomEvent.disableClickPropagation(box);
        return box;
      }
    });
    return new Ctrl();
  }

  /** Build a vertical Leaflet control bar of inline-styled buttons. */
  private toolbar(buttons: { html: string; title: string; onClick: () => void }[]): L.Control {
    const Ctrl = L.Control.extend({
      options: { position: 'topleft' as L.ControlPosition },
      onAdd: () => {
        const bar = L.DomUtil.create('div', 'leaflet-bar');
        bar.style.cssText = 'background:#fff;display:flex;flex-direction:column;';
        buttons.forEach(b => {
          const a = L.DomUtil.create('a', '', bar) as HTMLAnchorElement;
          a.href = '#';
          a.title = b.title;
          a.innerHTML = b.html;
          a.style.cssText = 'width:30px;height:30px;line-height:30px;text-align:center;font-size:15px;color:#15803d;cursor:pointer;text-decoration:none;font-weight:700;';
          L.DomEvent.on(a, 'click', (e: any) => { L.DomEvent.preventDefault(e); L.DomEvent.stopPropagation(e); b.onClick(); });
        });
        L.DomEvent.disableClickPropagation(bar);
        return bar;
      }
    });
    return new Ctrl();
  }

  private isFullscreen(): boolean {
    const doc: any = document;
    return !!(doc.fullscreenElement || doc.webkitFullscreenElement);
  }

  private toggleFullscreen(): void {
    const host: any = this.el.nativeElement;
    const doc: any = document;
    if (this.isFullscreen()) {
      (doc.exitFullscreen || doc.webkitExitFullscreen || (() => {})).call(doc);
    } else {
      const req = host.requestFullscreen || host.webkitRequestFullscreen;
      if (req) {
        try { Promise.resolve(req.call(host)).catch(() => this.fallbackFs(host)); }
        catch { this.fallbackFs(host); }
      } else {
        this.fallbackFs(host);
      }
    }
  }

  // Used only by browsers without the Fullscreen API (essentially none today).
  // The API path needs no CSS: the UA :fullscreen rule fills the element and we
  // just invalidateSize() to redraw. The fallback class is defined in the
  // component CSS (.sw-map-canvas.is-fullscreen / .ai-map-canvas.is-fullscreen).
  private fallbackFs(host: HTMLElement): void {
    host.classList.toggle('is-fullscreen');
    setTimeout(() => { try { this.map?.invalidateSize(); } catch { /* noop */ } }, 140);
  }

  private downloadPng(): void {
    if (!this.shoter) return;
    const raw = (this.data && (this.data.rep || this.data.title)) ? String(this.data.rep || this.data.title) : 'visit-map';
    const safe = (raw.replace(/[^a-z0-9 _-]/gi, '').replace(/\s+/g, '-').slice(0, 60)) || 'visit-map';
    this.shoter.takeScreen('image')
      .then((dataUrl: string) => {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `${safe}-visits.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(() => { /* capture failed (rare) — silently ignore */ });
  }

  private render(): void {
    const d = this.data;
    const host = this.el.nativeElement;
    if (!d || !Array.isArray(d.points) || !d.points.length || !host) return;

    const center: [number, number] = (Array.isArray(d.center) && d.center.length === 2) ? d.center : [25.2, 55.27];
    this.map = L.map(host, { scrollWheelZoom: true, attributionControl: true }).setView(center, 11);

    // Base layers — street (default) + satellite, with a toggle. Both free, no key.
    // CartoDB (basemap.cartocdn.com) is DNS-blocked from this network/region
    // (curl → HTTP 000 → blank tiles), so Street uses OpenStreetMap (globally
    // reachable). crossOrigin lets the PNG download capture the tiles without
    // tainting the canvas (OSM + Esri both send Access-Control-Allow-Origin: *).
    const street = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, crossOrigin: true, attribution: '© OpenStreetMap' }).addTo(this.map);
    const satellite = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', { maxZoom: 19, crossOrigin: true, attribution: 'Imagery © Esri' });
    L.control.layers({ 'Street': street, 'Satellite': satellite }, {}, { position: 'topright', collapsed: false }).addTo(this.map);

    // Full screen + download toolbar (sits under the zoom control, top-left).
    this.map.addControl(this.toolbar([
      { html: '⛶', title: 'Full screen (Esc to exit)', onClick: () => this.toggleFullscreen() },
      { html: '⤓', title: 'Download as image (PNG)', onClick: () => this.downloadPng() }
    ]));

    // Off-screen screenshoter — hidden button; we trigger it from our own control.
    try {
      this.shoter = (L as any).simpleMapScreenshoter({ hidden: true, preventDownload: true, mimeType: 'image/png' }).addTo(this.map);
    } catch { this.shoter = null; }

    // Redraw when entering/leaving native fullscreen.
    this.fsHandler = () => { setTimeout(() => { try { this.map?.invalidateSize(); } catch { /* noop */ } }, 160); };
    document.addEventListener('fullscreenchange', this.fsHandler);
    document.addEventListener('webkitfullscreenchange', this.fsHandler as any);

    const pts = d.points.filter((p: any) => typeof p.lat === 'number' && typeof p.lng === 'number');
    const maxCount = Math.max(1, ...pts.map((p: any) => p.count || 1));
    const totalCount = Math.max(1, pts.reduce((s: number, p: any) => s + (p.count || 1), 0));
    // Category point map (e.g. campaign garages) = colour-coded dots + legend.
    // Visit map = green count-bubbles. Detect by kind or per-point colour.
    const pointsMode = d.kind === 'points' || pts.some((p: any) => p.color);

    // Subtle heat glow underneath (density).
    const heatData = pts.map((p: any) => [p.lat, p.lng, Math.min(1, (p.count || 1) / maxCount)]);
    try { (L as any).heatLayer(heatData, { radius: 18, blur: 24, maxZoom: 13, minOpacity: 0.18, max: 1.0 }).addTo(this.map); } catch { /* heat plugin missing */ }

    // Clustered count bubbles: overlapping pins merge into a bubble showing the SUM
    // of their visits; they split apart as you zoom in.
    const cluster = (L as any).markerClusterGroup({
      maxClusterRadius: 42,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      iconCreateFunction: (c: any) => {
        const sum = c.getAllChildMarkers().reduce((s: number, m: any) => s + (m.options.visitCount || 1), 0);
        const t = Math.sqrt(sum / totalCount);
        return this.bubble(sum, Math.round(30 + 26 * t));   // 30–56px cluster bubble
      }
    });

    const bounds: [number, number][] = [];
    pts.forEach((p: any) => {
      bounds.push([p.lat, p.lng]);
      const label = String(p.label || p.customer || 'Location').replace(/</g, '&lt;');
      const weight = p.count || 1;
      let icon: L.DivIcon, popup: string;
      if (pointsMode) {
        icon = this.dot(p.color || '#16a34a', 14);   // colour-coded dot, no number
        const extra = p.detail ? String(p.detail) : (p.category ? String(p.category) : '');
        popup = `<b>${label}</b>${extra ? '<br/>' + extra.replace(/</g, '&lt;') : ''}`;
      } else {
        const size = Math.round(22 + 22 * Math.sqrt((p.count || 1) / maxCount));   // 22–44px single bubble
        icon = this.bubble(p.count, size);
        popup = `<b>${label}</b><br/>${p.count} visit${p.count === 1 ? '' : 's'}${p.last ? '<br/>last: ' + p.last : ''}`;
      }
      const m = L.marker([p.lat, p.lng], { icon, visitCount: weight } as any).bindPopup(popup);
      cluster.addLayer(m);
    });
    this.map.addLayer(cluster);
    if (Array.isArray(d.legend) && d.legend.length) { try { this.map.addControl(this.legendControl(d.legend)); } catch { /* noop */ } }
    if (bounds.length) { try { this.map.fitBounds(bounds as any, { padding: [24, 24], maxZoom: 14 }); } catch { /* noop */ } }

    setTimeout(() => { try { this.map?.invalidateSize(); } catch { /* noop */ } }, 140);

    // The host sits inside a chat bubble that keeps growing while the
    // typewriter reveals streamed text — watch for that resize and re-fit
    // the map, or tiles render for a stale container size (broken/jagged tiles).
    try {
      this.resizeObserver = new ResizeObserver(() => {
        try { this.map?.invalidateSize(); } catch { /* noop */ }
      });
      this.resizeObserver.observe(host);
    } catch { /* ResizeObserver unsupported — extremely rare today */ }

    this.inited = true;
  }
}
